import React, { useState } from "react";
import { useNavigate, Routes, Route, Navigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";

const HOBBIES = ["Reading", "Drawing", "Music", "Lego", "Gaming", "Dance", "Cooking", "Coding", "Storytelling", "Yoga"];
const SPORTS = ["Cricket", "Football", "Basketball", "Swimming", "Tennis", "Skating", "Athletics", "Badminton"];
const ACTIVITIES = ["Cycling", "Hiking", "Board games", "Puzzles", "Gardening", "Movies", "Travel", "Pets"];
const IMPROVEMENT_AREAS = [
  { key: "confidence",              en: "Confidence",            hi: "आत्मविश्वास" },
  { key: "discipline",              en: "Discipline",            hi: "अनुशासन" },
  { key: "hard_work",               en: "Hard Work",             hi: "मेहनत" },
  { key: "following_instructions",  en: "Following Instructions", hi: "निर्देशों का पालन" },
  { key: "selflessness",            en: "Selflessness",          hi: "निस्वार्थता" },
  { key: "focus",                   en: "Focus",                 hi: "एकाग्रता" },
  { key: "screen_habits",           en: "Screen Habits",         hi: "स्क्रीन की आदतें" },
];
const IMPROVEMENT_LIMIT = 2;

const CAREER_ASPIRATIONS = [
  "Doctor / Healer",
  "Scientist / Inventor",
  "Artist / Creator",
  "Athlete / Sports Star",
  "Entrepreneur / Business Leader",
  "Not sure yet",
];

function StepShell({ step, total, title, subtitle, children }) {
  return (
    <div className="min-h-screen px-6 py-8 max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-8">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full ${i < step ? "bg-[hsl(var(--neu-orange))]" : "bg-muted"}`}/>
        ))}
      </div>
      <h1 className="font-display text-4xl mb-2">{title}</h1>
      {subtitle && <p className="text-muted-foreground mb-8">{subtitle}</p>}
      {children}
    </div>
  );
}

function ParentStep() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/profile/parent", { name, mobile });
      nav("/onboarding/child");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <StepShell step={1} total={3} title="Tell us about you" subtitle="So we can address you the right way.">
      <form onSubmit={submit} className="space-y-4">
        <input required value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Your name" className="field" data-testid="onboarding-parent-name" autoFocus />
        <input value={mobile} onChange={(e) => setMobile(e.target.value)}
               placeholder="Mobile (optional)" className="field" data-testid="onboarding-parent-mobile"/>
        <button disabled={busy} className="btn-primary w-full mt-2" data-testid="onboarding-parent-next">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Continue <ArrowRight className="w-4 h-4"/></>}
        </button>
      </form>
    </StepShell>
  );
}

function ChipToggle({ items, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const on = selected.includes(it);
        return (
          <button type="button" key={it} onClick={() => onToggle(it)}
                  className={`px-4 py-2 rounded-full border text-sm transition-all ${
                    on ? "bg-[hsl(var(--neu-orange))] text-white border-transparent" : "bg-white/70 hover:bg-white"
                  }`}
                  data-testid={`${testIdPrefix}-${it.toLowerCase()}`}>
            {it}
          </button>
        );
      })}
    </div>
  );
}

function ChildStep() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("4-8");
  const [gender, setGender] = useState("");
  const [hobbies, setHobbies] = useState([]);
  const [sport, setSport] = useState("");
  const [activities, setActivities] = useState([]);
  const [improvementAreas, setImprovementAreas] = useState([]);
  const [aspirationChoice, setAspirationChoice] = useState("");
  const [aspirationCustom, setAspirationCustom] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (arr, setter) => (v) =>
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const toggleImprovement = (key) => {
    setImprovementAreas((arr) => {
      if (arr.includes(key)) return arr.filter((x) => x !== key);
      if (arr.length >= IMPROVEMENT_LIMIT) {
        toast.message(`Pick up to ${IMPROVEMENT_LIMIT} focus areas`);
        return arr;
      }
      return [...arr, key];
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const aspiration = aspirationChoice === "__custom__"
      ? (aspirationCustom.trim() || null)
      : (aspirationChoice || null);
    try {
      await api.post("/profile/child", {
        name, age_group: ageGroup, gender: gender || null,
        hobbies, favorite_sport: sport || null, favorite_activities: activities,
        improvement_areas: improvementAreas,
        career_aspiration: aspiration,
      });
      nav("/onboarding/notification");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <StepShell step={2} total={3} title="About your child" subtitle="So we can personalize each story to them.">
      <form onSubmit={submit} className="space-y-5">
        <input required value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Child's name" className="field" data-testid="onboarding-child-name" autoFocus />

        <div>
          <div className="text-sm text-muted-foreground mb-2">Age</div>
          <div className="grid grid-cols-2 gap-3">
            {["4-8", "9-13"].map((g) => (
              <button type="button" key={g} onClick={() => setAgeGroup(g)}
                      className={`p-4 rounded-2xl border text-left transition-all ${
                        ageGroup === g ? "bg-[hsl(var(--neu-orange))]/10 border-[hsl(var(--neu-orange))]" : "bg-white/70 hover:bg-white"
                      }`}
                      data-testid={`onboarding-child-age-${g}`}>
                <div className="font-display text-2xl">{g}</div>
                <div className="text-xs text-muted-foreground">years</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm text-muted-foreground mb-2">Gender (optional)</div>
          <div className="flex gap-2">
            {["boy", "girl", "other"].map((g) => (
              <button type="button" key={g} onClick={() => setGender(gender === g ? "" : g)}
                      className={`flex-1 px-3 py-2 rounded-full border text-sm capitalize ${
                        gender === g ? "bg-[hsl(var(--neu-orange))]/10 border-[hsl(var(--neu-orange))]" : "bg-white/70"
                      }`}
                      data-testid={`onboarding-child-gender-${g}`}>{g}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm text-muted-foreground mb-2">Hobbies</div>
          <ChipToggle items={HOBBIES} selected={hobbies}
                       onToggle={toggle(hobbies, setHobbies)}
                       testIdPrefix="onboarding-hobby"/>
        </div>

        <div>
          <div className="text-sm text-muted-foreground mb-2">Favorite sport</div>
          <ChipToggle items={SPORTS} selected={sport ? [sport] : []}
                       onToggle={(v) => setSport(sport === v ? "" : v)}
                       testIdPrefix="onboarding-sport"/>
        </div>

        <div>
          <div className="text-sm text-muted-foreground mb-2">Activities they love</div>
          <ChipToggle items={ACTIVITIES} selected={activities}
                       onToggle={toggle(activities, setActivities)}
                       testIdPrefix="onboarding-activity"/>
        </div>

        <div>
          <div className="text-sm text-muted-foreground mb-2">
            What does your child want to be when they grow up? <span className="text-xs">(optional)</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {CAREER_ASPIRATIONS.map((label) => {
              const on = aspirationChoice === label;
              return (
                <button type="button" key={label}
                        onClick={() => setAspirationChoice(on ? "" : label)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          on
                            ? "bg-[hsl(var(--neu-orange))] text-white border-transparent"
                            : "bg-white/70 hover:bg-white text-[hsl(var(--neu-ink))]"
                        }`}
                        data-testid={`onboarding-aspiration-${label.split(" ")[0].toLowerCase()}`}>
                  {label}
                </button>
              );
            })}
            <button type="button"
                    onClick={() => setAspirationChoice(aspirationChoice === "__custom__" ? "" : "__custom__")}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      aspirationChoice === "__custom__"
                        ? "bg-[hsl(var(--neu-orange))] text-white border-transparent"
                        : "bg-white/70 hover:bg-white text-[hsl(var(--neu-ink))]"
                    }`}
                    data-testid="onboarding-aspiration-custom">
              Add your own
            </button>
          </div>
          {aspirationChoice === "__custom__" && (
            <input type="text" className="field mt-3"
                    placeholder="Write what your child wants to be…"
                    value={aspirationCustom}
                    onChange={(e) => setAspirationCustom(e.target.value)}
                    data-testid="onboarding-aspiration-custom-input"/>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-sm text-muted-foreground">
              Areas of improvement for your child
            </div>
            <div className="text-xs text-muted-foreground">
              {improvementAreas.length}/{IMPROVEMENT_LIMIT}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Pick up to {IMPROVEMENT_LIMIT}. We&apos;ll gently reflect these in the questions and activity.
          </p>
          <div className="rounded-2xl bg-[hsl(var(--neu-orange))]/8 border border-[hsl(var(--neu-orange))]/25 px-3 py-2 mb-3 text-xs text-[hsl(var(--neu-ink))]">
            <span className="font-medium text-[hsl(var(--neu-orange))]">Note · </span>
            These two focus areas will guide your child&apos;s conversations for the next one-year plan.
          </div>
          <div className="flex flex-wrap gap-2">
            {IMPROVEMENT_AREAS.map((a) => {
              const on = improvementAreas.includes(a.key);
              const disabled = !on && improvementAreas.length >= IMPROVEMENT_LIMIT;
              return (
                <button type="button" key={a.key}
                        onClick={() => toggleImprovement(a.key)}
                        disabled={disabled}
                        className={`px-4 py-2 rounded-full border text-sm transition-all text-left ${
                          on
                            ? "bg-[hsl(var(--neu-orange))] text-white border-transparent"
                            : disabled
                              ? "bg-white/40 text-muted-foreground opacity-50 cursor-not-allowed"
                              : "bg-white/70 hover:bg-white"
                        }`}
                        data-testid={`onboarding-improvement-${a.key}`}>
                  <span>{a.en}</span>
                  <span className={`ml-1.5 text-xs ${on ? "text-white/85" : "text-muted-foreground"}`}>
                    {a.hi}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <button disabled={busy} className="btn-primary w-full mt-2" data-testid="onboarding-child-next">
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Continue <ArrowRight className="w-4 h-4"/></>}
        </button>
      </form>
    </StepShell>
  );
}

function NotificationStep() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [time, setTime] = useState("18:00");
  const [busy, setBusy] = useState(false);

  const TIMES = [
    { v: "17:00", l: "5 PM" }, { v: "18:00", l: "6 PM" },
    { v: "19:00", l: "7 PM" }, { v: "20:00", l: "8 PM" },
  ];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/profile/notification", { time_local: time, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      await refresh();
      toast.success("All set!");
      nav("/home");
    } catch (err) {
      toast.error("Could not save");
    } finally { setBusy(false); }
  };

  return (
    <StepShell step={3} total={3}
      title="When should we nudge you?"
      subtitle="A gentle Sunday reminder so your family ritual happens.">
      <div className="grid grid-cols-2 gap-3 mb-5">
        {TIMES.map((t) => (
          <button key={t.v} onClick={() => setTime(t.v)}
                   className={`p-4 rounded-2xl border text-left transition-all ${
                     time === t.v ? "bg-[hsl(var(--neu-orange))]/10 border-[hsl(var(--neu-orange))]" : "bg-white/70 hover:bg-white"
                   }`}
                   data-testid={`onboarding-time-${t.v}`}>
            <div className="font-display text-2xl">{t.l}</div>
            <div className="text-xs text-muted-foreground">Sunday</div>
          </button>
        ))}
      </div>
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="field mb-5" data-testid="onboarding-time-custom"/>

      <div className="card-soft p-5 mb-5 flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-[hsl(var(--neu-orange))] mt-0.5 shrink-0"/>
        <p className="text-sm text-muted-foreground">
          Your first session will be ready immediately. Future weeks unlock every Sunday. at your chosen time.
        </p>
      </div>

      <button onClick={save} disabled={busy} className="btn-primary w-full"
              data-testid="onboarding-finish-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Begin our journey"}
      </button>
    </StepShell>
  );
}

export default function Onboarding() {
  return (
    <Routes>
      <Route index element={<Navigate to="parent" replace />} />
      <Route path="parent" element={<ParentStep />} />
      <Route path="child" element={<ChildStep />} />
      <Route path="notification" element={<NotificationStep />} />
    </Routes>
  );
}
